import { createContext, useCallback, useContext, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";

import { useGetAllowAnonProductSubscriptionsForPlatform } from "@/api/orval/platforms/platforms";
import type { PlatformProductSubscription } from "@/api/orval/model";
import { isAuthorized } from "@/core/auth/auth";
import { matchAgelessMarketingPublicPath } from "@/modules/agelessMarketing/publicAccess";

import { getSubscriptionsFromWindowHost } from "./productSubscriptions.utils";

export const ProductSubscriptionsProvider = ({ children }: { children: React.ReactNode }) => {
  const { platformId } = useParams();
  const { pathname } = useLocation();
  const isAuthed = isAuthorized();
  // The endpoint is anonymous-safe (operational product list only), so the login-free
  // Ageless Marketing page reads it too — without it the page would fall back to guessing
  // subscriptions from the hostname and hide RepeatMD-only copy for practices that have it.
  const isPublicMarketingView = !isAuthed && matchAgelessMarketingPublicPath(pathname) !== null;

  const queryEnabled = !!platformId && (isAuthed || isPublicMarketingView);

  const { data: platformProductSubscriptions, isPending: isQueryLoading } =
    useGetAllowAnonProductSubscriptionsForPlatform(platformId ?? "", {
      query: { enabled: queryEnabled },
    });

  const isLoading = queryEnabled && isQueryLoading;

  const subscriptions = useMemo(() => {
    return platformProductSubscriptions == null
      ? getSubscriptionsFromWindowHost()
      : platformProductSubscriptions.products;
  }, [platformProductSubscriptions]);

  const hasSubscription = useCallback(
    (subscription: PlatformProductSubscription) => subscriptions.includes(subscription),
    [subscriptions],
  );

  const value = useMemo<ProductSubscriptionsContextValue>(
    () => ({ subscriptions, hasSubscription, isLoading }),
    [subscriptions, hasSubscription, isLoading],
  );

  return (
    <ProductSubscriptionsContext.Provider value={value}>
      {children}
    </ProductSubscriptionsContext.Provider>
  );
};

type ProductSubscriptionsContextValue = {
  subscriptions: PlatformProductSubscription[];
  hasSubscription: (subscription: PlatformProductSubscription) => boolean;
  isLoading: boolean;
};

const ProductSubscriptionsContext = createContext<ProductSubscriptionsContextValue | null>(null);

export const useProductSubscriptions = (): ProductSubscriptionsContextValue => {
  const context = useContext(ProductSubscriptionsContext);

  if (!context) {
    throw new Error("Missing ProductSubscriptionsProvider");
  }

  return context;
};

export const useSafeProductSubscriptions = (): ProductSubscriptionsContextValue | null => {
  return useContext(ProductSubscriptionsContext);
};
